import { supabase } from '../lib/supabase';
import { freshdeskEnrichmentService } from './freshdeskEnrichmentService';

export type CrmSearchEntityType = 'customer' | 'company' | 'traveler' | 'customer_via_traveler';

export interface CrmSearchResult {
  entityType: CrmSearchEntityType;
  id: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  score: number; // 0..1
  reason: string;
  matchedValue?: string | null;
  linkedCustomerId?: string | null;
  linkedTravelerId?: string | null;
}

export interface FreshdeskContactSuggestion {
  contactId: number;
  id?: number;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  score: number; // 0..100
  signals: string[];
  bestTicket?: { id: number; subject: string; created_at: string; url?: string } | null;
}

export interface CrmSearchResponse {
  local: CrmSearchResult[];
  freshdesk: FreshdeskContactSuggestion[];
  freshdeskMessage?: string;
}

interface CrmSearchRpcRow {
  entity_type: CrmSearchEntityType;
  id: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  score: number | null;
  reason: string | null;
  matched_value: string | null;
  linked_customer_id: string | null;
  linked_traveler_id: string | null;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || '');
}

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { at: number; value: CrmSearchResponse }>();

function cacheKey(q: string, options: {
  limit: number;
  enableFreshdeskFallback: boolean;
  freshdeskMinBestScore: number;
}): string {
  return JSON.stringify([q.trim().toLowerCase(), options]);
}

export function isStrongLocalMatch(r: CrmSearchResult): boolean {
  // Strong if exact-ish
  if (r.score >= 0.9) return true;
  return ['email_exact', 'phone_exact', 'id_exact', 'readable_id', 'full_name_exact'].includes(r.reason);
}

export async function crmSearch(query: string, options?: {
  limit?: number;
  enableFreshdeskFallback?: boolean;
  freshdeskMinBestScore?: number; // 0..100
}): Promise<CrmSearchResponse> {
  const q = (query || '').trim();
  if (!q) return { local: [], freshdesk: [] };

  const limit = options?.limit ?? 20;
  const enableFreshdeskFallback = options?.enableFreshdeskFallback !== false;
  const freshdeskMinBestScore = options?.freshdeskMinBestScore ?? 30;
  const key = cacheKey(q, { limit, enableFreshdeskFallback, freshdeskMinBestScore });
  const existing = cache.get(key);
  if (existing && Date.now() - existing.at < CACHE_TTL_MS) {
    return existing.value;
  }

  let local: CrmSearchResult[] = [];

  try {
    const { data, error } = await supabase.rpc('crm_search_parties', {
      search_term: q,
      limit_count: limit,
    });

    if (error) {
      const errorMessage = getErrorMessage(error);
      if (errorMessage.includes('function public.crm_search_parties') && errorMessage.includes('does not exist')) {
        console.error('CRM Search RPC missing - migration not applied?', error);
      } else {
        throw error;
      }
    } else {
      const rows = Array.isArray(data) ? (data as CrmSearchRpcRow[]) : [];
      local = rows.map((row) => ({
        entityType: row.entity_type,
        id: String(row.id),
        displayName: String(row.display_name || ''),
        email: row.email ?? null,
        phone: row.phone ?? null,
        score: Number(row.score ?? 0),
        reason: String(row.reason || 'unknown'),
        matchedValue: row.matched_value ?? null,
        linkedCustomerId: row.linked_customer_id ?? null,
        linkedTravelerId: row.linked_traveler_id ?? null,
      }));

      const linkedCustomerIds = Array.from(new Set(local.flatMap((result) => {
        if (result.entityType === 'customer') return [result.id];
        return result.linkedCustomerId ? [result.linkedCustomerId] : [];
      })));
      if (linkedCustomerIds.length > 0) {
        const { data: customerStates, error: stateError } = await supabase
          .from('customers')
          .select('id,merged_into_customer_id,is_merged,archived_at')
          .in('id', linkedCustomerIds);
        if (stateError) throw stateError;
        const inactiveIds = new Set((customerStates || [])
          .filter((customer) => customer.merged_into_customer_id || customer.is_merged || customer.archived_at)
          .map((customer) => String(customer.id)));
        local = local.filter((result) => {
          const customerId = result.entityType === 'customer' ? result.id : result.linkedCustomerId;
          return !customerId || !inactiveIds.has(customerId);
        });
      }
    }
  } catch (error: unknown) {
    console.error('CRM Search failed:', error);
    local = [];
    // Continue execution to at least return Freshdesk results if enabled
  }

  const bestLocal = local.reduce((max, r) => Math.max(max, r.score || 0), 0);
  const anyStrong = local.some(isStrongLocalMatch);

  let freshdesk: FreshdeskContactSuggestion[] = [];
  let freshdeskMessage: string | undefined;

  if (enableFreshdeskFallback && (!anyStrong || bestLocal < 0.75)) {
    try {
      const fd = await freshdeskEnrichmentService.smartContactSuggest({
        query: q,
        maxContacts: 5,
        maxTicketsPerContact: 25,
        maxDeepTickets: 2,
      });
      freshdeskMessage = fd.message;
      freshdesk = (fd.suggestions || [])
        .filter((s) => Number(s.score || 0) >= freshdeskMinBestScore)
        .map((s) => ({
          contactId: Number(s.contactId),
          name: s.contact?.name ?? null,
          email: s.contact?.email ?? null,
          phone: s.contact?.phone ?? null,
          score: Number(s.score || 0),
          signals: Array.isArray(s.signals) ? s.signals : [],
          bestTicket: s.bestTicket ?? null,
        }));
    } catch (fdErr) {
      console.warn('Freshdesk fallback search failed:', fdErr);
    }
  }

  const value: CrmSearchResponse = { local, freshdesk, freshdeskMessage };
  cache.set(key, { at: Date.now(), value });
  return value;
}

